import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { computeTemplateLineTotal, orpToRrp } from '@/lib/pricing';
import { getPricingDefaults } from '@/components/QuoteBuilder';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  Edit,
  Search,
  Package,
  FileText,
  ArrowLeft,
  Save,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'hours', label: 'Hours' },
  { value: 'liters', label: 'Liters' },
  { value: 'set', label: 'Set' },
];

const CATEGORY_OPTIONS = [
  { value: 'service', label: 'Service Packages' },
  { value: 'inspection', label: 'Inspections' },
  { value: 'repair', label: 'Repairs' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

export default function InvoiceTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'service',
    line_items: []
  });
  
  // New line item state — every structured field is OPTIONAL in the template.
  // ORP varies per supplier batch, RRP/margin/hrs/rate may differ per job.
  // The SM fills mandatory fields when the line lands on a real invoice.
  const [newItem, setNewItem] = useState({
    description: '',
    // Part number — printed on the customer invoice ("see what you paid
    // for") so SM can save it on the template for known parts. Optional
    // for labour-only rows.
    part_number: '',
    // Explicit row category — mirrors the invoice line-item picker so
    // 'part_only' rows skip labour fields and 'labour_only' rows skip
    // parts fields. When the template is applied to an invoice, the
    // SM doesn't have to re-pick the shape.
    line_category: 'part_and_labour',
    quantity: 1,
    unit: 'each',
    unit_price: 0,
    // Structured pricing fields — all optional in template.
    parts_supplier: '',
    parts_cost_price: '',
    parts_price: '',
    parts_margin_pct: '',
    labour_hours: '',
    labour_rate: '',
  });

  // Workshop ORP→RRP margin % from Pricing Defaults (auto-fill only).
  const [orpMarginPct, setOrpMarginPct] = useState(30);
  useEffect(() => {
    getPricingDefaults().then(d => {
      if (d?.orp_margin_pct != null) setOrpMarginPct(Number(d.orp_margin_pct));
    });
  }, []);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates?category=invoice_template`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.content || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || 
      (t.line_items?.[0]?.category || 'other') === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'service',
      line_items: []
    });
    setNewItem({
      description: '',
      part_number: '',
      line_category: 'part_and_labour',
      quantity: 1,
      unit: 'each',
      unit_price: 0,
      parts_supplier: '',
      parts_cost_price: '',
      parts_price: '',
      parts_margin_pct: '',
      labour_hours: '',
      labour_rate: '',
    });
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setEditingTemplate(null);
    setCreateDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.content || '',
      category: template.line_items?.[0]?.category || 'service',
      line_items: template.line_items || []
    });
    setCreateDialogOpen(true);
  };

  // Compute live line total — DELEGATES to the shared template-aware
  // helper so this Manage UI shows the EXACT same number as the
  // "Add Custom Item" picker. Helper handles the legacy `unit_price`-only
  // fallback so neither surface silently shows $0 for old rows.
  const lineTotal = (item) => {
    const row = {
      category: item.category || item.line_category || 'part_and_labour',
      quantity: parseFloat(item.quantity) || 1,
      parts_price: parseFloat(item.parts_price) || 0,
      parts_margin_pct: parseFloat(item.parts_margin_pct) || 0,
      labour_hours: parseFloat(item.labour_hours) || 0,
      labour_rate: parseFloat(item.labour_rate) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
    };
    return computeTemplateLineTotal(row, 0);
  };

  // Add line item to template — captures all structured fields (any of
  // which may be blank in the template — they're variables the SM fills
  // when the line lands on a real invoice).
  const addLineItem = () => {
    if (!newItem.description.trim()) {
      toast.error('Please enter item description');
      return;
    }

    // Category is now an EXPLICIT pick (Part only / Labour only / Part & Labour).
    // The picker doesn't only auto-infer from the filled fields any more —
    // it respects the SM's intent so an empty-ORP "Labour only" row stays
    // labour-only when applied to a real invoice. Falls back to inference
    // only if somehow line_category is missing.
    const explicit = newItem.line_category;
    let rowCategory = explicit;
    if (!rowCategory || !['part', 'labour', 'part_and_labour'].includes(rowCategory)) {
      const hasParts = !!(parseFloat(newItem.parts_price) || parseFloat(newItem.parts_cost_price));
      const hasLabour = !!(parseFloat(newItem.labour_hours) || parseFloat(newItem.labour_rate));
      rowCategory = hasParts && !hasLabour ? 'part'
        : !hasParts && hasLabour ? 'labour'
        : 'part_and_labour';
    }

    // Strip fields that don't apply to the chosen category so applying
    // the template doesn't sneak labour into a "part only" row.
    const isLabourOnly = rowCategory === 'labour';
    const isPartOnly = rowCategory === 'part';

    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          ...newItem,
          // Coerce numerics — keep null when blank so the apply path knows
          // "this is a variable the SM will fill at invoice time".
          quantity: parseFloat(newItem.quantity) || 1,
          unit_price: parseFloat(newItem.unit_price) || 0,
          part_number: isLabourOnly ? null : ((newItem.part_number || '').trim() || null),
          parts_supplier: isLabourOnly ? null : ((newItem.parts_supplier || '').trim() || null),
          parts_cost_price: isLabourOnly ? null : (newItem.parts_cost_price !== '' ? parseFloat(newItem.parts_cost_price) : null),
          parts_price: isLabourOnly ? null : (newItem.parts_price !== '' ? parseFloat(newItem.parts_price) : null),
          parts_margin_pct: isLabourOnly ? null : (newItem.parts_margin_pct !== '' ? parseFloat(newItem.parts_margin_pct) : null),
          labour_hours: isPartOnly ? null : (newItem.labour_hours !== '' ? parseFloat(newItem.labour_hours) : null),
          labour_rate: isPartOnly ? null : (newItem.labour_rate !== '' ? parseFloat(newItem.labour_rate) : null),
          category: rowCategory,
          item_id: `item_${Date.now()}`
        }
      ]
    }));

    setNewItem({
      description: '',
      part_number: '',
      line_category: 'part_and_labour',
      quantity: 1,
      unit: 'each',
      unit_price: 0,
      parts_supplier: '',
      parts_cost_price: '',
      parts_price: '',
      parts_margin_pct: '',
      labour_hours: '',
      labour_rate: '',
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Save template
  const saveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter template name');
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        content: formData.description.trim(),
        category: 'invoice_template',
        line_items: formData.line_items.map(item => ({
          description: item.description,
          // Part number — surfaced on the customer-facing invoice. Saved
          // on the template so SM doesn't retype it for known parts.
          part_number: (item.part_number || '').trim() || null,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0,
          // Persist STRUCTURED fields exactly as entered — null when blank
          // so the apply path on InvoiceEdit knows these are "variables the
          // SM fills at invoice time" rather than treating null as $0.
          parts_supplier: item.parts_supplier || null,
          parts_cost_price: item.parts_cost_price === '' || item.parts_cost_price == null ? null : parseFloat(item.parts_cost_price),
          parts_price: item.parts_price === '' || item.parts_price == null ? null : parseFloat(item.parts_price),
          parts_margin_pct: item.parts_margin_pct === '' || item.parts_margin_pct == null ? null : parseFloat(item.parts_margin_pct),
          labour_hours: item.labour_hours === '' || item.labour_hours == null ? null : parseFloat(item.labour_hours),
          labour_rate: item.labour_rate === '' || item.labour_rate == null ? null : parseFloat(item.labour_rate),
          category: item.category || formData.category
        }))
      };

      let response;
      if (editingTemplate) {
        response = await fetch(`${BACKEND_URL}/api/templates/${editingTemplate.template_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setCreateDialogOpen(false);
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save template');
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Duplicate template
  const duplicateTemplate = (template) => {
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (Copy)`,
      description: template.content || '',
      category: template.line_items?.[0]?.category || 'service',
      line_items: template.line_items || []
    });
    setCreateDialogOpen(true);
  };

  // Calculate template total — uses live structured formula per row.
  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + lineTotal(item), 0);
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Custom Item Templates</h1>
              <p className="text-slate-500 text-sm">Create reusable service packages for quick invoicing</p>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading templates...</div>
        ) : filteredTemplates.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No templates yet</h3>
            <p className="text-slate-500 mb-4">
              Create your first custom item template to speed up invoicing
            </p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map(template => (
              <Card key={template.template_id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <Badge variant="secondary" className="mt-1">
                      {CATEGORY_OPTIONS.find(c => c.value === (template.line_items?.[0]?.category || 'other'))?.label || 'Other'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => duplicateTemplate(template)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(template)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteTemplate(template.template_id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {template.content && (
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">{template.content}</p>
                )}
                
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs text-slate-500 mb-2">{template.line_items?.length || 0} items included:</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {(template.line_items || []).slice(0, 4).map((item, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-slate-700 truncate flex-1">{item.description}</span>
                        <span className="text-slate-500 ml-2">${computeTemplateLineTotal(item).toFixed(2)}</span>
                      </div>
                    ))}
                    {(template.line_items?.length || 0) > 4 && (
                      <p className="text-xs text-slate-400">+{template.line_items.length - 4} more items...</p>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-3 pt-2 border-t">
                    <span className="text-sm font-medium text-slate-700">Total:</span>
                    <span className="text-lg font-bold text-green-600">
                      ${calculateTotal(template.line_items || []).toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="!max-w-[80vw] w-[80vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Custom Item Template'}
            </DialogTitle>
            <DialogDescription>
              Create a package of items that can be quickly added to invoices
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Template Name & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Log Book Service"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this service package"
                className="mt-1"
              />
            </div>

            {/* Line Items */}
            <div>
              <Label className="mb-2 block">Line Items <span className="text-[10px] font-normal text-slate-500">· every structured field is optional in the template — SM enters real numbers on the invoice</span></Label>

              {/* Add New Item Form — TWO ROWS so the SM can capture supplier /
                  ORP / RRP / margin / labour at template-build time. */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-3">
                {/* Category pill row — matches the invoice line-item picker
                    so SM picks shape up-front. */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Line type:</Label>
                  {[
                    { value: 'part_and_labour', label: 'Part & Labour' },
                    { value: 'part', label: 'Part only' },
                    { value: 'labour', label: 'Labour only' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewItem(prev => ({ ...prev, line_category: opt.value }))}
                      data-testid={`tpl-newrow-category-${opt.value}`}
                      className={`text-xs px-3 py-1 rounded-full border transition ${
                        newItem.line_category === opt.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <span className="text-[10px] text-slate-500 ml-1">
                    {newItem.line_category === 'part' && '· Labour fields hidden — parts only'}
                    {newItem.line_category === 'labour' && '· Parts fields hidden — labour only'}
                    {newItem.line_category === 'part_and_labour' && '· Both parts + labour'}
                  </span>
                </div>

                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 md:col-span-4">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Engine Oil (5W30)"
                      className="mt-1"
                      data-testid="tpl-newrow-desc"
                    />
                  </div>
                  {/* Part number — printed on the customer invoice. Optional
                      for labour-only rows but mandatory for any known part. */}
                  {newItem.line_category !== 'labour' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Part number</Label>
                      <Input
                        value={newItem.part_number}
                        onChange={(e) => setNewItem(prev => ({ ...prev, part_number: e.target.value }))}
                        placeholder="e.g., REP-5W30-1L"
                        className="mt-1 font-mono"
                        data-testid="tpl-newrow-part-number"
                      />
                    </div>
                  )}
                  {newItem.line_category !== 'labour' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Supplier (optional)</Label>
                      <Input
                        value={newItem.parts_supplier}
                        onChange={(e) => setNewItem(prev => ({ ...prev, parts_supplier: e.target.value }))}
                        placeholder="e.g., Repco"
                        className="mt-1"
                        data-testid="tpl-newrow-supplier"
                      />
                    </div>
                  )}
                  <div className="col-span-3 md:col-span-2">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                      className="mt-1"
                      min="0.01"
                      step="0.01"
                      data-testid="tpl-newrow-qty"
                    />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Label className="text-xs">Unit</Label>
                    <Select
                      value={newItem.unit}
                      onValueChange={(v) => setNewItem(prev => ({ ...prev, unit: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_OPTIONS.map(u => (
                          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 items-end">
                  {newItem.line_category !== 'labour' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs text-rose-600">ORP $ (optional)</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={newItem.parts_cost_price}
                        onChange={(e) => {
                          const orp = e.target.value;
                          setNewItem(prev => {
                            // Auto-derive RRP using the shared formula
                            // (ORP × workshop ORP margin %) unless the SM
                            // has manually set an RRP that no longer matches
                            // the prior auto-derived value. Mirrors
                            // QuoteBuilder UX so templates use the SAME
                            // math everywhere.
                            const priorOrp = parseFloat(prev.parts_cost_price) || 0;
                            const priorAutoRrp = orpToRrp(priorOrp, orpMarginPct);
                            const currentRrp = prev.parts_price;
                            const rrpUntouched =
                              currentRrp === '' ||
                              currentRrp === null ||
                              currentRrp === undefined ||
                              (priorAutoRrp !== '' && Number(currentRrp) === Number(priorAutoRrp));
                            const next = { ...prev, parts_cost_price: orp };
                            if (rrpUntouched) next.parts_price = orpToRrp(orp, orpMarginPct);
                            return next;
                          });
                        }}
                        placeholder="supplier $"
                        className="mt-1 border-rose-200"
                        data-testid="tpl-newrow-orp"
                      />
                    </div>
                  )}
                  {newItem.line_category !== 'labour' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">RRP $</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={newItem.parts_price}
                        onChange={(e) => setNewItem(prev => ({ ...prev, parts_price: e.target.value }))}
                        placeholder="customer $"
                        className="mt-1"
                        data-testid="tpl-newrow-rrp"
                      />
                    </div>
                  )}
                  {newItem.line_category !== 'labour' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Margin %</Label>
                      <Input
                        type="number" min="0" step="1"
                        value={newItem.parts_margin_pct}
                        onChange={(e) => setNewItem(prev => ({ ...prev, parts_margin_pct: e.target.value }))}
                        placeholder="40+"
                        className="mt-1"
                        data-testid="tpl-newrow-margin"
                      />
                    </div>
                  )}
                  {newItem.line_category !== 'part' && (
                    <div className="col-span-6 md:col-span-2">
                      <Label className="text-xs">Labour hrs</Label>
                      <Input
                        type="number" min="0" step="0.25"
                        value={newItem.labour_hours}
                        onChange={(e) => setNewItem(prev => ({ ...prev, labour_hours: e.target.value }))}
                        placeholder="0"
                        className="mt-1"
                        data-testid="tpl-newrow-hours"
                      />
                    </div>
                  )}
                  {newItem.line_category !== 'part' && (
                    <div className="col-span-8 md:col-span-2">
                      <Label className="text-xs">Lab rate $/h</Label>
                      <Input
                        type="number" min="0" step="0.5"
                        value={newItem.labour_rate}
                        onChange={(e) => setNewItem(prev => ({ ...prev, labour_rate: e.target.value }))}
                        placeholder="125"
                        className="mt-1"
                        data-testid="tpl-newrow-rate"
                      />
                    </div>
                  )}
                  <div className="col-span-4 md:col-span-2 flex flex-col gap-1 ml-auto">
                    <Label className="text-xs">Live total</Label>
                    <div className="h-9 px-2 flex items-center justify-end font-bold tabular-nums bg-white border rounded text-emerald-700" data-testid="tpl-newrow-livetotal">
                      ${lineTotal(newItem).toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={addLineItem} size="sm" data-testid="tpl-newrow-add">
                    <Plus className="h-4 w-4 mr-1" /> Add Line
                  </Button>
                </div>
              </div>

              {/* Items List */}
              {formData.line_items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead className="w-28">Part #</TableHead>
                      <TableHead className="w-24">Supplier</TableHead>
                      <TableHead className="w-16 text-right">Qty</TableHead>
                      <TableHead className="w-20 text-right text-rose-600">ORP</TableHead>
                      <TableHead className="w-20 text-right">RRP</TableHead>
                      <TableHead className="w-16 text-right">Mrg%</TableHead>
                      <TableHead className="w-14 text-right">Hrs</TableHead>
                      <TableHead className="w-16 text-right">Rate</TableHead>
                      <TableHead className="w-24 text-right">Total</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.line_items.map((item, index) => {
                      const catLabel = item.category === 'labour' ? 'Labour'
                        : item.category === 'part' ? 'Part'
                        : item.category === 'part_and_labour' ? 'Part + Lab'
                        : '—';
                      const catColor = item.category === 'labour' ? 'bg-indigo-100 text-indigo-700'
                        : item.category === 'part' ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700';
                      return (
                      <TableRow key={index}>
                        <TableCell className="font-medium" data-testid={`tpl-row-${index}-desc`}>{item.description}</TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${catColor}`} data-testid={`tpl-row-${index}-category`}>{catLabel}</span>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-700" data-testid={`tpl-row-${index}-part`}>{item.part_number || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{item.parts_supplier || '—'}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right text-rose-700">{item.parts_cost_price != null ? `$${Number(item.parts_cost_price).toFixed(2)}` : '—'}</TableCell>
                        <TableCell className="text-right">{item.parts_price != null ? `$${Number(item.parts_price).toFixed(2)}` : '—'}</TableCell>
                        <TableCell className="text-right">{item.parts_margin_pct != null ? `${item.parts_margin_pct}%` : '—'}</TableCell>
                        <TableCell className="text-right">{item.labour_hours != null ? `${item.labour_hours}h` : '—'}</TableCell>
                        <TableCell className="text-right">{item.labour_rate != null ? `$${Number(item.labour_rate).toFixed(0)}` : '—'}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          ${lineTotal(item).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={10} className="text-right font-semibold">
                        Template Total:
                      </TableCell>
                      <TableCell className="text-right font-bold text-lg text-green-600">
                        ${calculateTotal(formData.line_items).toFixed(2)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <div className="border rounded-lg p-8 text-center text-slate-500">
                  <Package className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>No items added yet. Add items above.</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} className="bg-green-600 hover:bg-green-700">
              <Save className="h-4 w-4 mr-2" />
              {editingTemplate ? 'Update Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
